<template>
    <UIGridContainer>
        <UIGridRow>
            <UIGridCol md="8">
                <h1>{{ translate(`category.${category}.h1`) }}</h1>
                <div class="subtitle">{{ translate(`category.${category}.description`) }}</div>
                <SearchResults :products="products" :loading="loading" :total="total"></SearchResults>
            </UIGridCol>
            <UIGridCol md="4">
                <FeatureContainer class="d-none d-md-block"></FeatureContainer>
                <aside class="mt-4 mt-md-0">
                    <h2 class="mt-0">{{ $category.getName(category) }}</h2>
                    <div class="description small text-light" v-html="translate(`category.${category}.body`)"></div>
                </aside>
            </UIGridCol>
        </UIGridRow>
    </UIGridContainer>
</template>
<script>
import { UIGridContainer, UIGridRow, UIGridCol } from '@dundle/ui/components';
import SearchMixin from '~/mixins/SearchMixin';
import FeatureContainer from '~/components/feature/FeatureContainer.vue';
import { SearchResults } from '~/components/search';
import TranslationMixin from '~/mixins/TranslationMixin';
import BreadcrumbMixin from '~/mixins/BreadcrumbMixin';

export default {
    components: {
        UIGridContainer,
        UIGridRow,
        UIGridCol,
        SearchResults,
        FeatureContainer,
    },

    mixins: [SearchMixin, TranslationMixin, BreadcrumbMixin],

    async asyncData({ app, route, redirect, error }) {
        if (route.params.slug === 'gamekeys') {
            redirect(301, app.$locale.path({ name: 'category-slug', params: { slug: 'gamecards' } }));
        }
        const resolvedSlug = await app.$category.resolveSlug(route.params.slug);
        if (!resolvedSlug) {
            return error({ message: 'Page not found', statusCode: 404 });
        } else if (resolvedSlug !== route.params.slug) {
            redirect(app.$locale.path({ name: 'category-slug', params: { slug: resolvedSlug } }));
        }
        const category = resolvedSlug;
        const filterRegion = !!app.$locale.currentLocale.target;
        const results = await app.$api.brand.getPopularPaginated(category, 0, 100, false, filterRegion);
        const promiseArray = results.data.map(async item => await app.$api.brand.getBySlug(item.slug));
        const total = results.total;
        const products = await Promise.all(promiseArray);
        return { category, filterRegion, total, products };
    },
    data() {
        return {
            totalResults: 0,
            limit: 0,
            offset: 0,
            loading: false,
            query: null,
        };
    },
    meta: {
        footer: 'version-2',
    },
    head() {
        return {
            title: this.translate(`category.${this.category}.metaTitle`),
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.translate(`category.${this.category}.metaDesc`),
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.$locale.isTarget() ? 'index, follow' : 'noindex, nofollow',
                },
            ],
            link: [
                {
                    hid: 'canonical',
                    rel: 'canonical',
                    href: this.$locale.url({
                        ...this.$locale.reverseRoute(this.$route),
                        query: this.$route.query.p ? { p: this.$route.query.p } : undefined,
                        deriveDomain: true,
                    }),
                },
                this.linkRelPrev ? this.linkRelPrev : '',
                this.linkRelNext ? this.linkRelNext : '',
            ],
        };
    },

    computed: {
        linkRelNext() {
            if (this.totalResults > this.page * this.limit) {
                return {
                    hid: 'next',
                    rel: 'next',
                    href: this.$locale.url({
                        ...this.$locale.reverseRoute(this.$route),
                        query: { p: this.$route.query.p || 1 + 1 },
                        deriveDomain: true,
                    }),
                };
            } else {
                return null;
            }
        },
        linkRelPrev() {
            if (this.page > 1) {
                return {
                    hid: 'prev',
                    rel: 'prev',
                    href: this.$locale.url({
                        ...this.$locale.reverseRoute(this.$route),
                        query: this.$route.query.p > 2 ? { p: this.$route.query.p - 1 } : undefined,
                        deriveDomain: true,
                    }),
                };
            } 
            
            return null;
        },
    },
    mounted() {
        this.$gtm?.push({ event: `page_view_category`, value: this.category });
    },

    breadcrumb({ app }, data) {
        return [
            {
                name: app.$locale.regio.toLowerCase(),
                link: app.$locale.path('index'),
                url: 'https://' + app.$locale.domain + app.$locale.path('index'),
            },
            {
                name: app.$category.getName(data.category),
                url: 'https://' + app.$locale.domain + app.$category.getPath(data.category),
            },
        ];
    },

    httpCaching: {
        maxAge: 60 * 30,
    },

    pageCaching: {
        maxAge: 60 * 30,
    },
};
</script>

<style lang="scss" scoped>
.breadcrumb {
    margin-bottom: 2rem;

    .separator {
        font-size: 0.75rem;
    }

    @media screen and (min-width: 768px) {
        margin-top: -1rem;
    }
}

@media screen and (max-width: 767px) {
    .feature-container {
        display: none;
    }
}
</style>

<i18n src="@dundle/locale/data/search.json"></i18n>
<i18n src="@dundle/locale/data/categories.json" />
